import React, { useState, useRef, useEffect, useContext } from "react";
import "../../styles/productivity.scss";

import { Row, Col, Image } from "react-bootstrap";
import Skeleton from "react-loading-skeleton";
import { commonGetService, handleFilterApi } from "../../utils/properties";
import { useAppSelector, useAppDispatch } from "../../app/hooks";
import { showToast } from "../../features/Toaster/toastslice";
import { activityContext, AccessInfo } from "../../pages/Dashboard";
import { useNavigate } from "react-router-dom";

interface ProductiveDetails {

  emp_code?: string;
  employee_code: string;
  emp_id: number;
  emp_name: string;
  productive_time: string;
  unproductive_time: string;
  worked_time: string;
  idle_time: string;
  time_percentage: number;
  designation: string;
}

const Productivity = () => {

  const [highestProductive, setHighestProductive] = useState<ProductiveDetails[]>([]);
  const [highestUnproductive, setHighestUnproductive] = useState<ProductiveDetails[]>([]);
  const [productiveLoading, setProductiveLoading] = useState(false);
  const [unProductiveLoading, setUnProductiveLoading] = useState(false);
  const [initialLoad, setInitialLoad] = useState(true);

  const targetRef = useRef<HTMLDivElement>(null);
  const dispatch = useAppDispatch();
  const Navigate = useNavigate();

  const filterData = useAppSelector((state) => state.filterData);
  const { startDate, endDate, empDeviceId, filterType, organization, department, employeeIds } =
    filterData;

  const accessInfo: AccessInfo = useContext(activityContext)?.accessInfo as AccessInfo;
  const isTablet = useContext(activityContext)?.isTablet;

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInitialLoad(false);

          if (accessInfo["Highest Productive Time"]) getMostProductiveList();
          if (accessInfo["Highest Unproductive Time"]) getMostUnproductiveList();

          observer.disconnect();
        }
      },
      { threshold: 0.25 }
    );

    if (targetRef.current) observer.observe(targetRef.current);

    return () => observer.disconnect();
  }, [
    startDate,
    endDate,
    filterType,
    organization,
    empDeviceId,
    department,
    employeeIds,
    accessInfo,
  ]);

  const getMostProductiveList = () => {
    setProductiveLoading(true);
    commonGetService(
      `/api/1.0/dashboard/trends/most/productive?${handleFilterApi(
        startDate,
        endDate,
        filterType,
        organization,
        empDeviceId,
        department,
        false
      )}`
    ).then((res) => {
      setProductiveLoading(false);
      if (res.status === 200) {
        setHighestProductive(res.data);
      }
      else {
        dispatch(showToast({ message: "Something went wrong.", status: "retry", visible: true }));
      }
    });
  };

  const getMostUnproductiveList = () => {
    setUnProductiveLoading(true);
    commonGetService(
      `/api/1.0/dashboard/trends/most/unproductive?${handleFilterApi(
        startDate,
        endDate,
        filterType,
        organization,
        empDeviceId,
        department,
        false
      )}`
    ).then((res) => {
      setUnProductiveLoading(false);
      if (res.status === 200) setHighestUnproductive(res.data);
      else {
        dispatch(showToast({ message: "Something went wrong.", status: "retry", visible: true }));
      }
    });
  };

  const handleNavigation = (e: any, type: string) => {
    e.stopPropagation();
    Navigate(`/reports`, {
      state: {
        reportType: type,
        employeeIds,
        Alluser: organization === "All Organizations",
        startDate,
        endDate,
      },
    });
  };

  return (
    <>

      {/* <div className="dashboard-wrapper" ref={targetRef}> */}
      <div className="dashboard-wrapper" ref={targetRef} style={{ marginTop: 0, paddingTop: 0 }}>
        <Row className="m-0 g-0">

          {/* ---------------------- HIGHEST PRODUCTIVE ---------------------- */}

          {accessInfo["Highest Productive Time"] && (
            <Col lg className={`panel ${highestProductive.length == 0 ? " empty-panel" : "non-empty-panel"}`}>
              <div className="panel-header">
                <h5>Highest Productivity (by %)</h5>
                <a className="cursor-pointer" onClick={(e) => handleNavigation(e, "productive_report")}>
                  View full report
                </a>
              </div>

              <div className={`cards-grid ${highestProductive.length == 0 ? "empty-card" : ""}`}>
                {(productiveLoading || highestProductive.length) ? (
                  productiveLoading ? (
                    [...Array(5)].map((_, i) => (
                      <div className="card" key={i}>
                        <Skeleton height={20} width="60%" />
                        <Skeleton height={12} width="40%" />
                        <Skeleton height={20} width="20%" />
                        <Skeleton height={10} />
                      </div>
                    ))
                  ) : (
                    highestProductive.map((item, idx) => (
                      <div className="card" key={idx}>
                        <div className="top">
                          <div>
                            {/* <h4>{item.emp_name}</h4> */}
                            <p
                              style={{ cursor: 'pointer' }}
                              onClick={() => {
                                Navigate('/employeeOverview', { state: { empCode: item?.emp_code ?? item?.employee_code } });
                              }}
                            >
                              {item?.emp_name}
                            </p>
                            <p>{item.designation}</p>
                          </div>
                          <div className="icon">↗</div>
                        </div>

                        <div className="value">{item.time_percentage}%</div>

                        <div className="bar green">
                          <div style={{ width: `${item.time_percentage}%` }}></div>
                        </div>
                      </div>
                    ))
                  )
                ) : (
                  <div className="no-data-row">
                    <div className="no-data-image-wrapper">

                      <Image src="/images/dashboard/user_icon.png" />
                    </div>
                    <p>No users to show</p>
                  </div>
                )}
              </div>
            </Col>
          )}

          {/* ---------------------- HIGHEST UNPRODUCTIVE ---------------------- */}
          {accessInfo["Highest Unproductive Time"] && (
            <Col lg className={`panel ${highestUnproductive.length == 0 ? "" : "non-empty-panel"}`}>
              <div className="panel-header">
                <h5>Highest Unproductivity (by %)</h5>
                <a
                  className="cursor-pointer"
                  onClick={(e) => handleNavigation(e, "unproductive_report")}
                >
                  View full report
                </a>
              </div>

              <div className={`cards-grid ${highestUnproductive.length == 0 ? "empty-card" : ""}`}>
                {(unProductiveLoading || highestUnproductive.length) ? (
                  unProductiveLoading ? (
                    [...Array(5)].map((_, i) => (
                      <div className="card" key={i}>
                        <Skeleton height={20} width="60%" />
                        <Skeleton height={12} width="40%" />
                        <Skeleton height={20} width="20%" />
                        <Skeleton height={10} />
                      </div>
                    ))
                  ) : (
                    highestUnproductive.map((item, idx) => (
                      <div className="card" key={idx}>
                        <div className="top">
                          <div>
                            {/* <h4>{item.emp_name}</h4> */}
                            <p
                              style={{ cursor: 'pointer' }}
                              onClick={() => Navigate('/employeeOverview', { state: { empCode: item?.emp_code ?? item?.employee_code } })}
                            >
                              {item?.emp_name}
                            </p>
                            <p>{item.designation}</p>
                          </div>
                          <div className="icon">↗</div>
                        </div>

                        <div className="value">{item.time_percentage}%</div>

                        <div className="bar red">
                          <div style={{ width: `${item.time_percentage}%` }}></div>
                        </div>
                      </div>
                    ))
                  )
                ) : (
                  <div className="no-data-row">
                    <div className="no-data-image-wrapper">
                      <Image src="/images/dashboard/user_icon.png" />
                    </div>
                    <p>No users to show</p>
                  </div>
                )}
              </div>
            </Col>
          )}
        </Row>
      </div>
    </>
  );
};

export default Productivity;
